// Redesigned NewEvent.js
// Changes: 
// - Bootstrap for forms, accordions (via cards), responsive rows/cols.
// - Icons: FaCalendar, FaClock, etc. for labels.
// - User-friendly: Accordions for sections, validation on submit, placeholders.
// - Responsive: col-md-6 for inputs, mobile-friendly.
// - Fixed custom duration handling, added isprivate toggle.

import React, { useState } from 'react';
import apiService from '../../services/ApiServices';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaInfoCircle, FaSave, FaPlus, FaMoneyBillWave, FaImage, FaSortNumericDown } from 'react-icons/fa';
import LoadingOverlay from '../shared/LoadingOverlay';
import useLoading from '../shared/useLoading';

const NewEvent = () => {
  const navigate = useNavigate();
  const { isLoading, withLoading } = useLoading();

  // Event Details
  const [nameSection, setNameSection] = useState(true);
  const [eventName, setEventName] = useState('');
  const [duration, setDuration] = useState({ normalvalue: '15 mins', customvalue: ['15', 'mins'] });
  const [locationtype, setLocationtype] = useState('GOOGLE_MEET_AND_CALENDAR');
  const [locationDetails, setLocationDetails] = useState('');

  const [isprivate, setIsprivate] = useState(false);
  const [approvalNeeded, setApprovalNeeded] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [eventAmt, setEventAmt] = useState(0);
  const [isDefaultConfirm, setIsDefaultConfirm] = useState(true);
  const [customConfirm, setCustomConfirm] = useState('');
  const [description, setDescription] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [eventImageFile, setEventImageFile] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);

  // Event Settings
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [dateRangeType, setDateRangeType] = useState('days');
  const [dateRangeDays, setDateRangeDays] = useState(60);
  const [bufferBefore, setBufferBefore] = useState(15);
  const [bufferAfter, setBufferAfter] = useState(15);
  const [startTimeIncrement, setStartTimeIncrement] = useState(15);
  const [inviteeBuffer, setInviteeBuffer] = useState(4);

  // Notifications
  const [isEmailRemindersOpen, setIsEmailRemindersOpen] = useState(false);
  const [emailRemindersEnabled, setEmailRemindersEnabled] = useState(true);
  const [reminderSubject, setReminderSubject] = useState('Reminder: Event Name is scheduled to start at Event Time');
  const [reminderBody, setReminderBody] = useState(`Hi My Name,
This is a friendly reminder that your Event Name will start at Event Time. Here are the event details:
Event Description: Event Description
Your information: Question And Answers
Reschedule Url | Cancel Url`);
  const [reminderTiming, setReminderTiming] = useState(24);

  // Custom form (after Event Settings)
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [customFields, setCustomFields] = useState([]);
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');

  const durationOptions = [
    { value: '15 mins', label: '15 mins' },
    { value: '30 mins', label: '30 mins' },
    { value: '1 hour', label: '1 hour' },
    { value: '1 hour 30 mins', label: '1 hour 30 mins' },
    { value: '2 hours', label: '2 hours' },
    { value: 'custom', label: 'Custom' }
  ];

  const addCustomField = ({ label, type }) => {
    setCustomFields((prev) => [...prev, { id: Date.now(), label, type }]);
  };

  const openAddFieldModal = () => {
    setNewFieldLabel('');
    setNewFieldType('text');
    setIsAddFieldModalOpen(true);
  };

  const closeAddFieldModal = () => {
    setIsAddFieldModalOpen(false);
  };

  const confirmAddField = () => {
    if (!newFieldLabel.trim()) {
      alert('Field label is required.');
      return;
    }
    addCustomField({ label: newFieldLabel.trim(), type: newFieldType });
    closeAddFieldModal();
  };

  const removeCustomField = (id) => {
    setCustomFields((prev) => prev.filter((field) => field.id !== id));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventImageFile(file);
      setEventImagePreview(URL.createObjectURL(file));
    }
  };

  const handleEventSubmit = withLoading(async (e) => {
    e.preventDefault();
    if (!eventName.trim()) {
      alert('Event name is required.');
      return;
    }
    if (locationtype == 'IN_PERSON' && locationDetails.trim() == '') {
      alert('Location details are required.');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('title', eventName);
      formData.append('duration', JSON.stringify(duration));
      formData.append('isprivate', isprivate);
      formData.append('approval_needed', approvalNeeded);
      formData.append('is_paid', isPaid);
      formData.append('event_amt', isPaid ? Number(eventAmt) : 0);
      formData.append('description', description);
      formData.append('locationtype', locationtype);
      formData.append('location_details', locationDetails);
      formData.append('order_by', orderBy !== '' ? parseInt(orderBy, 10) : '');
      if (eventImageFile) {
        formData.append('event_image', eventImageFile);
      }

      const response = await apiService.post('/api/events/events/', formData);
      console.log('Event created:', response);
      alert('Event created successfully!');
      navigate('/events'); // Redirect to list
    } catch (error) {
      console.error('Error creating event:', error);
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to create event.';
      alert(errMsg);
    }
  });

  const handleSettingsSubmit = withLoading(async (e) => {
    e.preventDefault();
    try {
      const response = await apiService.post('/api/event-settings', {
        dateRangeType,
        dateRangeDays,
        bufferBefore,
        bufferAfter,
        startTimeIncrement,
        inviteeBuffer,
        is_default_confirm: isDefaultConfirm,
        custom_confirm: customConfirm
      });
      console.log('Settings saved:', response.data);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings.');
    }
  });

  const handleNotificationsSave = () => {
    if (!reminderSubject.trim() || !reminderBody.trim()) {
      alert('Subject and body are required.');
      return;
    }
    alert('Notifications settings saved!');
  };

  return (
    <div className="container py-4">
      <LoadingOverlay isLoading={isLoading} />
      <div className="row justify-content-center">
        <div className="col-lg-10">
          <h1 className="text-center mb-4">New Event</h1>

          {/* Event Details Accordion */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header p-3 text-bg-primary d-flex justify-content-between align-items-center" onClick={() => setNameSection(!nameSection)} style={{ cursor: 'pointer' }}>
              <h5 className="mb-0"><FaCalendarAlt className="me-2" /> Event Details</h5>
              <span>{nameSection ? '−' : '+'}</span>
            </div>
            {nameSection && (
              <form onSubmit={handleEventSubmit} className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Event Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={eventName}
                      onChange={(e) => setEventName(e.target.value)}
                      placeholder="Enter event name"
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaClock className="me-1" /> Meeting Duration <FaInfoCircle className="text-primary ms-1" title="Info" />
                    </label>
                    <select
                      className="form-select"
                      value={duration.normalvalue}
                      onChange={(e) => setDuration({ normalvalue: e.target.value, customvalue: duration.customvalue })}
                    >
                      {durationOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    {duration.normalvalue === 'custom' && (
                      <div className="mt-2 d-flex">
                        <input
                          type="number"
                          className="form-control w-auto me-2"
                          value={duration.customvalue[0]}
                          onChange={(e) => setDuration({ ...duration, customvalue: [e.target.value, duration.customvalue[1]] })}
                          placeholder="e.g., 45"
                          style={{ width: '80px' }}
                        />
                        <select
                          className="form-select w-auto"
                          value={duration.customvalue[1]}
                          onChange={(e) => setDuration({ ...duration, customvalue: [duration.customvalue[0], e.target.value] })}
                          style={{ width: '100px' }}
                        >
                          <option value="mins">mins</option>
                          <option value="hours">hours</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaMapMarkerAlt className="me-1" /> Location <FaInfoCircle className="text-primary ms-1" title="Info" />
                    </label>
                    <select
                      className="form-select"
                      value={locationtype}
                      onChange={(e) => setLocationtype(e.target.value)}
                    >
                      <option value="GOOGLE_MEET_AND_CALENDAR">Google Meet</option>
                      <option value="ZOOM_MEETING">Zoom</option>
                      <option value="MICROSOFT_TEAMS">Microsoft Teams</option>
                      <option value="IN_PERSON">In Person</option>
                      <option value="PHONE">Phone</option>
                    </select>
                  </div>
                  {
                    locationtype === 'IN_PERSON' && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          <FaMapMarkerAlt className="me-1" /> Location Details <FaInfoCircle className="text-primary ms-1" title="Info" />
                        </label>
                        <input
                          className="form-control"
                          type='text'
                          value={locationDetails}
                          onChange={(e) => setLocationDetails(e.target.value)}
                        >
                        </input>
                      </div>
                    )
                  }
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Private Event</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isprivate}
                        onChange={(e) => setIsprivate(e.target.checked)}
                      />
                      <label className="form-check-label">Make this event private</label>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Require Approval</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={approvalNeeded}
                        onChange={(e) => setApprovalNeeded(e.target.checked)}
                      />
                      <label className="form-check-label">Approval needed before booking</label>
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaSortNumericDown className="me-1" /> Order By <FaInfoCircle className="text-primary ms-1" title="Display order for this event" />
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderBy}
                      onChange={(e) => setOrderBy(e.target.value)}
                      placeholder="e.g., 1"
                      min={0}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <FaImage className="me-1" /> Event Image <FaInfoCircle className="text-primary ms-1" title="Upload an image for this event" />
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {eventImagePreview && (
                      <div className="mt-2">
                        <img
                          src={eventImagePreview}
                          alt="Event preview"
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="col-12 mb-3">
                    <label className="form-label">Description <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter event description"
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary">
                  <FaSave className="me-1" /> Save Event
                </button>
              </form>
            )}
          </div>

          {/* Event Settings Accordion */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header p-3 text-bg-primary d-flex justify-content-between align-items-center" onClick={() => setIsSettingOpen(!isSettingOpen)} style={{ cursor: 'pointer' }}>
              <h5 className="mb-0"><FaClock className="me-2" /> Event Settings</h5>
              <span>{isSettingOpen ? '−' : '+'}</span>
            </div>
            {isSettingOpen && (
              <>
                <form onSubmit={handleSettingsSubmit} className="card-body">
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Date Range <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateRange"
                          checked={dateRangeType === 'days'}
                          onChange={() => setDateRangeType('days')}
                        />
                        <label className="form-check-label">
                          {dateRangeDays} Calendar Days <input
                            type="number"
                            className="form-control d-inline-block w-auto ms-2"
                            style={{ width: '80px' }}
                            value={dateRangeDays}
                            onChange={(e) => setDateRangeDays(Number(e.target.value))}
                          /> into the future
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dateRange"
                          checked={dateRangeType === 'indefinite'}
                          onChange={() => setDateRangeType('indefinite')}
                        />
                        <label className="form-check-label">Indefinitely into the future</label>
                      </div>
                    </div>
                    {/* Add similar rows for buffer, increment, inviteeBuffer */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Buffer Before Event <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                      <select className="form-select" value={bufferBefore} onChange={(e) => setBufferBefore(Number(e.target.value))}>
                        <option value={15}>15 min</option>
                        <option value={30}>30 min</option>
                        <option value={45}>45 min</option>
                        <option value={60}>60 min</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Buffer After Event <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                      <select className="form-select" value={bufferAfter} onChange={(e) => setBufferAfter(Number(e.target.value))}>
                        <option value={15}>15 min</option>
                        <option value={30}>30 min</option>
                        <option value={45}>45 min</option>
                        <option value={60}>60 min</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Time Increments <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                      <select className="form-select" value={startTimeIncrement} onChange={(e) => setStartTimeIncrement(Number(e.target.value))}>
                        <option value={15}>15 min</option>
                        <option value={30}>30 min</option>
                        <option value={45}>45 min</option>
                        <option value={60}>60 min</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Invitees can't schedule within <FaInfoCircle className="text-primary ms-1" title="Info" /></label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control w-auto"
                          value={inviteeBuffer}
                          onChange={(e) => setInviteeBuffer(Number(e.target.value))}
                          style={{ width: '80px' }}
                        />
                        <span className="input-group-text">mins of an event start time</span>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label"><FaMoneyBillWave className="me-1" /> Paid Event</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isPaid}
                          onChange={(e) => {
                            setIsPaid(e.target.checked);
                            if (!e.target.checked) setEventAmt(0);
                          }}
                        />
                        <label className="form-check-label">This is a paid event</label>
                      </div>
                    </div>
                    {isPaid && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Event Amount <FaInfoCircle className="text-primary ms-1" title="Amount to charge for this event" /></label>
                        <div className="input-group">
                          <span className="input-group-text">₹</span>
                          <input
                            type="number"
                            className="form-control"
                            value={eventAmt}
                            onChange={(e) => setEventAmt(Number(e.target.value))}
                            min={0}
                            placeholder="e.g., 500"
                            required
                          />
                        </div>
                      </div>
                    )}
                    <div className="col-md-6 mb-3">
                      <label className="form-label"><FaMoneyBillWave className="me-1" /> Default confirmation message</label>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isDefaultConfirm}
                          onChange={(e) => {
                            setIsDefaultConfirm(e.target.checked);
                            if (!e.target.checked) setCustomConfirm('');
                          }}
                        />
                      </div>
                    </div>
                    {/* Custom confirmation message field - show only when custom is selected */}
                    {!isDefaultConfirm && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Custom confirmation message <FaInfoCircle className="text-primary ms-1" title="Message to show after booking" /></label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={customConfirm}
                          onChange={(e) => setCustomConfirm(e.target.value)}
                          placeholder="Enter custom confirmation message"
                          required
                        />
                      </div>
                    )}
                  </div>
                  {/* 
                  <hr className="my-4" />

                  <h5 className="mb-3"><FaInfoCircle className="me-2" /> Invitee Form</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        placeholder="Requester name"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={formEmail}
                        onChange={(e) => setFormEmail(e.target.value)}
                        placeholder="Requester email"
                        required
                      />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formDescription}
                        onChange={(e) => setFormDescription(e.target.value)}
                        placeholder="Short note for this form"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Additional Fields</label>
                    {customFields.length === 0 && (
                      <p className="text-muted mb-2">No additional fields added.</p>
                    )}
                    {customFields.map((field) => {
                      const inputId = `custom-field-${field.id}`;
                      return (
                        <div key={field.id} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-muted small">{field.type}</span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => removeCustomField(field.id)}
                            >
                              Remove
                            </button>
                          </div>

                          {field.type === 'checkbox' ? (
                            <div className="form-check">
                              <input className="form-check-input" type="checkbox" id={inputId} />
                              <label className="form-check-label" htmlFor={inputId}>{field.label}</label>
                            </div>
                          ) : (
                            <>
                              <label className="form-label" htmlFor={inputId}>{field.label}</label>
                              <input
                                id={inputId}
                                type={field.type}
                                className="form-control"
                                placeholder={field.type === 'number' ? 'Enter a number' : 'Enter text'}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={openAddFieldModal}>
                      <FaPlus className="me-1" /> Add Field
                    </button>
                  </div> */}

                  <button type="submit" className="btn btn-primary">
                    <FaSave className="me-1" /> Save Settings
                  </button>
                </form>

                {isAddFieldModalOpen && (
                  <>
                    <div
                      className="modal fade show"
                      style={{ display: 'block' }}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="addFieldModalTitle"
                    >
                      <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title" id="addFieldModalTitle">Add Field</h5>
                            <button type="button" className="btn-close" aria-label="Close" onClick={closeAddFieldModal} />
                          </div>
                          <div className="modal-body">
                            <div className="mb-3">
                              <label className="form-label">Field Type</label>
                              <select
                                className="form-select"
                                value={newFieldType}
                                onChange={(e) => setNewFieldType(e.target.value)}
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="checkbox">Checkbox</option>
                              </select>
                            </div>
                            <div className="mb-3">
                              <label className="form-label">Label</label>
                              <input
                                type="text"
                                className="form-control"
                                value={newFieldLabel}
                                onChange={(e) => setNewFieldLabel(e.target.value)}
                                placeholder="e.g., Company name"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={closeAddFieldModal}>
                              Cancel
                            </button>
                            <button type="button" className="btn btn-primary" onClick={confirmAddField}>
                              <FaPlus className="me-1" /> Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-backdrop fade show" onClick={closeAddFieldModal} />
                  </>
                )}
              </>
            )}
          </div>

          {/* Notifications Policy Accordion */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header p-3 text-bg-primary d-flex justify-content-between align-items-center" onClick={() => setIsEmailRemindersOpen(!isEmailRemindersOpen)} style={{ cursor: 'pointer' }}>
              <h5 className="mb-0"><FaInfoCircle className="me-2" /> Notifications Policy</h5>
              <span>{isEmailRemindersOpen ? '−' : '+'}</span>
            </div>
            {isEmailRemindersOpen && (
              <div className="card-body">
                <p className="text-muted mb-3">Email Reminders, Calendar Invitations, Create no Event in CRM</p>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <p className="text-muted">An invitee will receive a reminder email before a scheduled event at specified times.</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <button className="btn btn-link p-0">Close</button>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={emailRemindersEnabled}
                          onChange={(e) => setEmailRemindersEnabled(e.target.checked)}
                        />
                        <label className="form-check-label">Enabled</label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Subject <span className="text-primary">+Variables</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={reminderSubject}
                      onChange={(e) => setReminderSubject(e.target.value)}
                      placeholder="Enter subject"
                    />
                    <button className="btn btn-link p-0 mt-1">Restore Default</button>
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Body <span className="text-primary">+Variables</span></label>
                    <textarea
                      className="form-control"
                      rows="5"
                      value={reminderBody}
                      onChange={(e) => setReminderBody(e.target.value)}
                      placeholder="Enter body"
                    />
                    <button className="btn btn-link p-0 mt-1">Restore Default</button>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Timing</label>
                    <select
                      className="form-select"
                      value={reminderTiming}
                      onChange={(e) => setReminderTiming(Number(e.target.value))}
                    >
                      {[...Array(24)].map((_, i) => (
                        <option key={i} value={i + 1}>{i + 1} Hour(s) before</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button className="btn btn-outline-primary mb-3">
                  <FaPlus className="me-1" /> Add Another Reminder
                </button>
                <button onClick={handleNotificationsSave} className="btn btn-success">
                  <FaSave className="me-1" /> Save Settings
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewEvent;
